import type { IAgencyProduct } from '~/types/agencies';

export type EtaiementPrestationPriceUnit = 'm2' | 'flat' | 'unit';

export type EtaiementPrestationSupport = 'Béton' | 'Enpierrement';

export interface IEtaiementPrestationRow {
  productType?: string;
  productId?: string;
  height?: number | null;
  support?: EtaiementPrestationSupport | '';
  level?: string;
  priceUnit?: EtaiementPrestationPriceUnit | '';
  quantity?: number | null;
  unitPrice?: number | null;
  lineTotal?: number | null;
}

export const PRESTATION_PRICE_UNIT_OPTIONS = [
  { label: 'm²', value: 'm2' },
  { label: 'Forfait', value: 'flat' },
  { label: 'unité', value: 'unit' },
] as const;

export const PRESTATION_SUPPORT_OPTIONS = [
  { label: 'Béton', value: 'Béton' },
  { label: 'Enpierrement', value: 'Enpierrement' },
] as const;

export const isPrestationFlatPriceUnit = (
  priceUnit: string | undefined,
): boolean => priceUnit === 'flat';

export const getPrestationUnitPrice = (
  agencyProduct: IAgencyProduct | undefined,
  priceUnit: string | undefined,
): number => {
  if (!agencyProduct || !priceUnit) return 0;
  switch (priceUnit) {
    case 'm2':
      return Number(agencyProduct.prestationPricePerM2) || 0;
    case 'flat':
      return Number(agencyProduct.prestationFlatPrice) || 0;
    case 'unit':
      return Number(agencyProduct.prestationPricePerUnit) || 0;
    default:
      return 0;
  }
};

export const computePrestationLineTotal = (
  unitPrice: number,
  quantity: number | null | undefined,
  priceUnit?: string,
): number => {
  const unit = Number(unitPrice) || 0;
  if (unit <= 0) return 0;
  if (isPrestationFlatPriceUnit(priceUnit)) {
    return Math.round(unit * 100) / 100;
  }
  const qty = Number(quantity) || 0;
  if (qty <= 0) return 0;
  return Math.round(unit * qty * 100) / 100;
};

export const emptyPrestationRow = (): IEtaiementPrestationRow => ({
  productType: '',
  productId: '',
  height: null,
  support: '',
  level: '',
  priceUnit: '',
  quantity: null,
  unitPrice: null,
  lineTotal: null,
});

const isValidObjectIdString = (value: unknown): value is string => (
  typeof value === 'string' && /^[a-f\d]{24}$/i.test(value)
);

export const isPrestationRowPersistable = (
  row: IEtaiementPrestationRow | null | undefined,
): boolean => isValidObjectIdString(row?.productId ? String(row.productId) : '');

export const sanitizePrestationsForPersistence = (
  rows: IEtaiementPrestationRow[] | null | undefined,
): IEtaiementPrestationRow[] => {
  if (!rows?.length) return [];
  return rows
    .filter(isPrestationRowPersistable)
    .map((row) => {
      const priceUnit = row.priceUnit === 'm2' || row.priceUnit === 'flat' || row.priceUnit === 'unit'
        ? row.priceUnit
        : '';
      return {
        productType: row.productType?.trim() || '',
        productId: String(row.productId),
        height: row.height ?? null,
        support: row.support || '',
        level: row.level?.trim() || '',
        priceUnit,
        quantity: row.quantity ?? null,
        unitPrice: row.unitPrice ?? null,
        lineTotal: row.lineTotal ?? null,
      };
    });
};
